import { TFunction } from 'react-i18next';
import { zonedTimeToUtc, formatInTimeZone } from 'date-fns-tz';
import { set } from 'date-fns';
import { DEFAULT_HOUR, Time } from 'UI/TimePicker/constants';
import { AppointmentType, isAppointmentTypeRecurring } from 'shared/recurring';
import { generateDateWithTimezone } from 'utils';
import { useParams } from 'react-router-dom';
import { useAppSelector } from 'hooks/hooks';
import { useWidgetPriceQuote } from 'hooks/priceQuote';
import UserService from 'services/user.service';
import { centsToAmount } from 'shared/money';
import { selectHasTimeRangeSelection } from 'store/availableTimeRange/selectors';
import {
  CreateLeadAdditionalInformation,
  CreateLeadBody,
  PriceQuoteRequest,
  PriceQuoteResponse,
} from 'store/widget/widget.types';
import { AppointmentQuestion, WidgetStep } from './Widget.types';
import { WidgetState } from './Widget.store';

export const normalise = (value: number, state: WidgetState) => ((value - 0) * 100) / (state.widgetSteps.length - 0);
export const userBrowserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

function getRequestedDate(
  hasTimeRangeSelection: boolean | undefined,
  requestedDate: Date | undefined,
  requestedTime: Time | undefined | null,
  buildingTimezone: string,
) {
  if (hasTimeRangeSelection && requestedDate && buildingTimezone) {
    return generateDateWithTimezone(requestedDate, DEFAULT_HOUR, buildingTimezone);
  }
  if (requestedDate && requestedTime) {
    return generateDateWithTimezone(requestedDate, requestedTime.timeForDate, buildingTimezone);
  }
  return undefined;
}

export interface LeadSubmission {
  body: CreateLeadBody;
  priceCents: number;
}

export interface LeadSubmissionInput {
  appointmentGroupId: string | undefined;
  appointmentQuestions: readonly AppointmentQuestion[];
  createdAtTimezone: string;
  quoteArgs: PriceQuoteRequest | null;
  quote: Pick<PriceQuoteResponse, 'priceCents'> | undefined;
  recurrenceEndDate: string | undefined;
  requestedDate: string | undefined;
}

export function toLeadAdditionalInformation(
  appointmentQuestions: readonly AppointmentQuestion[],
): CreateLeadAdditionalInformation[] {
  return appointmentQuestions.map(({ question, answer }) => ({ question, answer }));
}

export function buildLeadSubmission({
  appointmentGroupId,
  appointmentQuestions,
  createdAtTimezone,
  quoteArgs,
  quote,
  recurrenceEndDate,
  requestedDate,
}: LeadSubmissionInput): LeadSubmission | null {
  if (!quoteArgs || !quote || !requestedDate) {
    return null;
  }

  return {
    body: {
      additionalInformation: toLeadAdditionalInformation(appointmentQuestions),
      addonIds: quoteArgs.addonIds,
      appointmentGroupId: appointmentGroupId ? Number(appointmentGroupId) : undefined,
      createdAtTimezone,
      price: centsToAmount(quote.priceCents),
      recurrenceType: quoteArgs.recurrenceType,
      requestedDate,
      recurrenceEndDate,
      timeRange: quoteArgs.timeRange,
      variantId: quoteArgs.variantId,
    },
    priceCents: quote.priceCents,
  };
}

export const useGetBookingPayload = (state: WidgetState): LeadSubmission | null => {
  const { appointmentGroupId } = useParams<{ appointmentGroupId: string }>();
  const hasTimeRangeEnabled = useAppSelector(selectHasTimeRangeSelection);
  const { args, quote } = useWidgetPriceQuote();
  const user = UserService.getLocalUser();

  const { appointmentQuestions, selectedRequestedDate, selectedEndDate, selectedRequestedTime } = state;

  const requestedDateFormatted = getRequestedDate(
    hasTimeRangeEnabled,
    selectedRequestedDate,
    selectedRequestedTime,
    user?.buildingTimezone || user?.building?.timezone,
  );

  const recurrenceEndDateFormatted =
    selectedEndDate && (user?.buildingTimezone || user?.building?.timezone)
      ? (() => {
          const buildingTimezone = user?.buildingTimezone || user?.building?.timezone!;
          const dateString = formatInTimeZone(selectedEndDate, buildingTimezone, 'yyyy-MM-dd');
          const dateOnly = new Date(`${dateString}T00:00:00`);
          const endOfDayDate = set(dateOnly, { hours: 23, minutes: 59, seconds: 59, milliseconds: 999 });
          return zonedTimeToUtc(endOfDayDate, buildingTimezone).toISOString();
        })()
      : undefined;

  return buildLeadSubmission({
    appointmentGroupId,
    appointmentQuestions,
    createdAtTimezone: userBrowserTimezone,
    quoteArgs: args,
    quote,
    recurrenceEndDate: recurrenceEndDateFormatted,
    requestedDate: requestedDateFormatted,
  });
};

const WIDGET_STEP_TITLE_MAP = {
  [WidgetStep.ADDITIONAL_INFORMATION]: 'widget.step.title.additionalinformation',
  [WidgetStep.ADDON]: 'widget.step.title.addons',
  [WidgetStep.APPOINTMENT]: 'widget.step.title.appointment',
  [WidgetStep.DATE_AND_TIME]: 'widget.step.title.dateandtime',
  [WidgetStep.INDUSTRY]: 'widget.step.title.industry',
  [WidgetStep.SUMMARY]: 'widget.step.title.summary',
  [WidgetStep.VARIANT]: 'widget.step.title.variant',
  [WidgetStep.PAYMENT_SUCCESS]: undefined,
  [WidgetStep.PAYMENT_ERROR]: undefined,
};

export function getWidgetStepTitleTranslated(
  t: TFunction<'translation'>,
  type: WidgetStep,
  params?: { appointmentType?: AppointmentType },
) {
  // Note: On `DATE_AND_TIME` step possible 2 titles depending on `appointmentType`.
  if (type === WidgetStep.DATE_AND_TIME && params?.appointmentType) {
    const key = isAppointmentTypeRecurring(params.appointmentType)
      ? 'widget.step.title.dateandtime.recurring'
      : 'widget.step.title.dateandtime.onetime';
    return t(key);
  }
  const key = WIDGET_STEP_TITLE_MAP[type];
  return t(key ?? 'common.label.empty');
}
